// client/src/AdminRolloutSetsTab.js
import React, { useState, useEffect } from 'react';
import { authedFetch } from './utils/authedFetch';
import { apiUrl } from './utils/apiBase';

const AdminRolloutSetsTab = () => {
  // Rollout sets configuration state
  const [currentConfig, setCurrentConfig] = useState(null);
  const [newTargetSets, setNewTargetSets] = useState('');
  const [configLoading, setConfigLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadCurrentConfig();
  }, []);

  // Rollout sets configuration functionality
  const loadCurrentConfig = async () => {
    try {
      setConfigLoading(true);
      const response = await authedFetch(apiUrl('rolloutSetsSelector'), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      
      if (data.success) {
        setCurrentConfig(data.config);
        setNewTargetSets(data.config.targetSets.toString());
      } else {
        setError('Failed to load current configuration');
      }
    } catch (error) {
      console.error('Error loading config:', error);
      setError('Error loading configuration');
    } finally {
      setConfigLoading(false);
    }
  };

  const handleUpdate = async () => {
    try {
      setUpdating(true);
      setMessage('');
      setError('');

      const targetSets = parseInt(newTargetSets);
      
      // Validation
      if (isNaN(targetSets) || targetSets < 10000) {
        setError('Target sets must be at least 10,000');
        return;
      }

      if (targetSets % 10000 !== 0) {
        setError('Target sets must be in 10,000 increments (e.g., 20000, 50000, 100000)');
        return;
      }

      if (targetSets > 10000000) {
        setError('Target sets cannot exceed 10,000,000');
        return;
      }

      const response = await authedFetch(apiUrl('rolloutSetsSelector'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          targetSets: targetSets,
          updatedBy: 'admin@lottologic.ai',
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        setMessage(`✅ ${data.result.message}`);
        await loadCurrentConfig(); // Reload the current config
      } else {
        setError(data.error || 'Failed to update target sets');
      }
    } catch (error) {
      console.error('Error updating config:', error);
      setError('Error updating configuration');
    } finally {
      setUpdating(false);
    }
  };

  const formatNumber = (num) => {
    return num ? num.toLocaleString() : '0';
  };

  const formatDate = (date) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleString();
  };

  return (
    <div style={{ padding: '2rem' }}>
      <h2>Rollout Sets Management</h2>
      
      {/* Rollout Sets Configuration Section */}
      <div style={{ marginBottom: '3rem' }}>
        <h3 style={{ color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '0.5rem' }}>
          Target Sets Configuration
        </h3>
        <p style={{ color: '#666', marginBottom: '1.5rem' }}>
          Configure how many number sets are generated for both Powerball and Mega Millions draws.
          Changes take effect on the next scheduled generation run.
        </p>

        {configLoading ? (
          <p>Loading current configuration...</p>
        ) : (
          <>
            {/* Current Configuration */}
            <div style={{ 
              backgroundColor: '#f8f9fa', 
              border: '1px solid #dee2e6', 
              borderRadius: '8px', 
              padding: '1.5rem', 
              marginBottom: '1.5rem' 
            }}>
              <h4 style={{ marginTop: 0, color: '#333' }}>Current Configuration</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <strong>Target Sets:</strong> {formatNumber(currentConfig?.targetSets)}
                </div>
                <div>
                  <strong>Last Updated:</strong> {formatDate(currentConfig?.lastUpdated)}
                </div>
                <div>
                  <strong>Updated By:</strong> {currentConfig?.updatedBy || 'System Default'}
                </div>
                <div>
                  <strong>Status:</strong> {currentConfig?.isDefault ? '🔧 Default Setting' : '✅ Custom Setting'}
                </div>
              </div>
            </div>

            {/* Update Form */}
            <div style={{ 
              backgroundColor: '#fff', 
              border: '1px solid #dee2e6', 
              borderRadius: '8px', 
              padding: '1.5rem',
              marginBottom: '1.5rem'
            }}>
              <h4 style={{ marginTop: 0, color: '#333' }}>Update Target Sets</h4>
              
              <div style={{ marginBottom: '1rem' }}>
                <label htmlFor="targetSets" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  New Target Sets:
                </label>
                <input
                  type="number"
                  id="targetSets"
                  value={newTargetSets}
                  onChange={(e) => setNewTargetSets(e.target.value)}
                  placeholder="e.g., 20000, 50000, 100000"
                  min="10000"
                  step="10000"
                  style={{ 
                    width: '200px', 
                    padding: '0.5rem', 
                    border: '1px solid #ccc', 
                    borderRadius: '4px',
                    fontSize: '1rem'
                  }}
                  disabled={updating}
                />
                <div style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.25rem' }}>
                  Must be in 10,000 increments (minimum: 10,000, maximum: 10,000,000)
                </div>
              </div>

              <button
                onClick={handleUpdate}
                disabled={updating || !newTargetSets}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: updating ? '#6c757d' : '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  cursor: updating ? 'not-allowed' : 'pointer',
                  opacity: updating ? 0.6 : 1,
                }}
              >
                {updating ? 'Updating...' : 'Update Target Sets'}
              </button>

              {/* Messages */}
              {message && (
                <div style={{ 
                  marginTop: '1rem', 
                  padding: '0.75rem', 
                  backgroundColor: '#d4edda', 
                  border: '1px solid #c3e6cb', 
                  borderRadius: '4px', 
                  color: '#155724' 
                }}>
                  {message}
                </div>
              )}

              {error && (
                <div style={{ 
                  marginTop: '1rem', 
                  padding: '0.75rem', 
                  backgroundColor: '#f8d7da', 
                  border: '1px solid #f5c6cb', 
                  borderRadius: '4px', 
                  color: '#721c24' 
                }}>
                  {error}
                </div>
              )}
            </div>

            {/* Quick Settings */}
            <div style={{ 
              padding: '1rem', 
              backgroundColor: '#e9f7ff', 
              border: '1px solid #b8daff', 
              borderRadius: '8px' 
            }}>
              <h4 style={{ marginTop: 0, color: '#004085' }}>Quick Settings:</h4>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {[20000, 50000, 100000, 200000, 500000].map(count => (
                  <button
                    key={count}
                    onClick={() => setNewTargetSets(count.toString())}
                    disabled={updating}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: '#fff',
                      border: '1px solid #007bff',
                      borderRadius: '4px',
                      color: '#007bff',
                      cursor: 'pointer',
                      fontSize: '0.875rem'
                    }}
                  >
                    {formatNumber(count)}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminRolloutSetsTab;